import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Collapse,
  Container,
  Row,
  Spinner,
} from "react-bootstrap";
import { ChevronDown, ChevronUp, Users as UsersIcon } from "react-feather";
import { Helmet } from "react-helmet-async";
import useLocalStorage from "../../hooks/useLocalStorage";
import MonthlyUsageChart from "./analytics/MonthlyUsageChart";
import MonthlySessionsChart from "./analytics/MonthlySessionsChart";
import WidgetWrapper from "./analytics/WidgetWrapper";
import TopMonthlyUsers from "./analytics/TopMonthlyUsers";
import ActiveUsers from "./analytics/ActiveUsers";
import useAnalytics from "./analytics/AnalyticsHook";
import NewUser from "./NewUser";
import UsersSearchTable from "./analytics/UsersSearchTable";

const Users = () => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useLocalStorage("userAnalyticsOpen", true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsSessionHistories, setAnalyticsSessionHistories] = useState(
    []
  );
  const [analyticsCurrentMonthCode, setAnalyticsCurrentMonthCode] = useState(
    []
  );
  const [analyticsActiveUsers, setAnalyticsActiveUsers] = useState([]);
  const { FetchAnalyticsData } = useAnalytics();

  const [showNewUser, setShowNewUser] = useState(false);
  const handleCloseNewUser = () => setShowNewUser(false);
  const handleShowNewUser = () => setShowNewUser(true);

  const [activeUsersTitle, setActiveUsersTitle] = useState("Active Users");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      try {
        setAnalyticsLoading(true);

        var responseData = await FetchAnalyticsData();

        if (cancelled) return;

        setAnalyticsCurrentMonthCode(responseData.currentMonthCode);
        setAnalyticsSessionHistories(responseData.sessionHistories);
        setAnalyticsActiveUsers(responseData.activeUsers);
      } catch (error) {
        console.log(error);
      } finally {
        setAnalyticsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (analyticsActiveUsers) {
      const activeUsersCount = analyticsActiveUsers.length;
      setActiveUsersTitle(
        activeUsersCount.toString() +
          " Active User" +
          (activeUsersCount > 1 ? "s" : "")
      );
    }
  }, [analyticsActiveUsers]);

  return (
    <React.Fragment>
      <Helmet defer={false} title="Users" />
      <Container fluid className="p-0">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1 className="h3 mb-0">
            <UsersIcon className={"me-2"} />
            Users
            {loading && (
              <Spinner
                animation="border"
                variant="primary"
                size="sm"
                className="p-0 m-0"
              />
            )}
          </h1>
          <Button className="text-nowrap" onClick={handleShowNewUser}>
            <FontAwesomeIcon icon={faPlus} fixedWidth className="me-1" />
            Create New
          </Button>
        </div>

        <Card className="p-0">
          <Card.Header className="p-0">
            <div className="d-flex justify-content-between align-items-end bg-primary p-2">
              <div>
                <h3 className="text-white">
                  User Analytics {analyticsLoading && <Spinner size="sm" />}
                </h3>
              </div>
              <Button
                variant="primary"
                onClick={() => setOpen(!open)}
                aria-controls="example-collapse-text"
                aria-expanded={open}
              >
                &nbsp;
                {!open ? <ChevronDown></ChevronDown> : <ChevronUp></ChevronUp>}
                &nbsp;
              </Button>
            </div>
          </Card.Header>
          <Collapse in={open} mountOnEnter={true} unmountOnExit={true}>
            <Card.Body>
              <Row>
                <WidgetWrapper
                  title={activeUsersTitle}
                  expandRoute={"/users/activeusers"}
                  loading={analyticsLoading}
                >
                  <ActiveUsers activeUsers={analyticsActiveUsers} />
                </WidgetWrapper>
                <WidgetWrapper
                  title={"Monthly Usage"}
                  expandRoute={"/users/monthlyusage"}
                  loading={analyticsLoading}
                >
                  <MonthlyUsageChart
                    analyticsData={analyticsSessionHistories}
                  />
                </WidgetWrapper>
                <WidgetWrapper
                  title={"Monthly Sessions"}
                  expandRoute={"/users/monthlysessions"}
                  loading={analyticsLoading}
                >
                  <MonthlySessionsChart
                    analyticsData={analyticsSessionHistories}
                  />
                </WidgetWrapper>
                <WidgetWrapper
                  title={"Top Monthly Users"}
                  expandRoute={"/users/topmonthlyusers"}
                  loading={analyticsLoading}
                >
                  <TopMonthlyUsers
                    currentMonthCode={analyticsCurrentMonthCode}
                    monthlyHistories={analyticsSessionHistories}
                  />
                </WidgetWrapper>
              </Row>
            </Card.Body>
          </Collapse>
        </Card>

        {/* <pre style={{ maxHeight: "300px", overflowY: "scroll" }}>{JSON.stringify(analyticsSessionHistories, null, " ")} </pre> */}
        <Card className="bg-white p-3">
          <UsersSearchTable
            setLoading={setLoading}
            defaultSortBy={[
              { id: "current_month.total_session_seconds", desc: true },
            ]}
          />
        </Card>
      </Container>
      <NewUser
        title={"Create new User"}
        show={showNewUser}
        handleClose={handleCloseNewUser}
      />
    </React.Fragment>
  );
};

export default Users;
